import { useMutation } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  Input,
  Textarea,
} from '@/components/ui';
import { ontologyApi } from '@/lib/api';

/**
 * Spec S09 — DE Analyst editor for new classes / properties.
 *
 * The flow is intentionally explicit:
 *   1. fill the form,
 *   2. save the draft (DE),
 *   3. run a sandbox,
 *   4. request promotion,
 *   5. PO approves on a separate screen.
 *
 * Phase 7.5 ships steps 1-4; step 5 lives in a PO-only page (skill editor for
 * skills; the same `request-promotion` → `promote` API powers it here too).
 */
export function OntologyDraftEditor() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const presetLabel = params.get('label') ?? '';
  const presetGap = params.get('gap') ?? undefined;
  const presetCandidate = params.get('candidate') ?? '';
  const presetFrequency = params.get('frequency') ?? '';

  const [operationType, setOperationType] = useState<'add_class' | 'add_property'>('add_class');
  const [iri, setIri] = useState(toCamelCase(presetLabel));
  const [label, setLabel] = useState(presetLabel);
  const [parentIri, setParentIri] = useState(presetCandidate || 'ett:Asset');
  const [comment, setComment] = useState(buildInitialComment(presetLabel, presetFrequency));
  const [draftId, setDraftId] = useState<string | null>(null);
  const [draftStatus, setDraftStatus] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () =>
      ontologyApi.createDraft({
        operation_type: operationType,
        iri: ensureEttPrefix(iri),
        label,
        parent_iri: parentIri,
        gap_issue_id: presetGap,
        payload: { comment },
      }),
    onSuccess: (created) => {
      setDraftId(created.draft_id);
      setDraftStatus(created.status);
    },
  });

  const runSandbox = useMutation({
    mutationFn: () => ontologyApi.runSandbox(draftId!),
    onSuccess: (out) => setDraftStatus(out.status),
  });

  const requestPromotion = useMutation({
    mutationFn: () => ontologyApi.requestPromotion(draftId!),
    onSuccess: (out) => setDraftStatus(out.status),
  });

  return (
    <div className="space-y-4 p-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-display text-brand-900">New ontology draft</h1>
          <p className="mt-1 text-body text-slate-600">
            Drafts are reviewed by the Product Owner before promotion. Source:{' '}
            <Link to="/studio/ontology" className="text-accent-700 underline">
              gap queue
            </Link>
            .
          </p>
        </div>
        {draftStatus && <Badge variant="info">{draftStatus}</Badge>}
      </header>

      <Card>
        <CardHeader>
          <h2 className="text-h2 text-brand-900">Operation</h2>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-2">
            {(['add_class', 'add_property'] as const).map((op) => (
              <Button
                key={op}
                variant={operationType === op ? 'primary' : 'secondary'}
                size="sm"
                onClick={() => setOperationType(op)}
              >
                {op.replace('_', ' ')}
              </Button>
            ))}
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            <div>
              <label className="block text-h4 text-slate-700">IRI</label>
              <Input
                value={iri}
                onChange={(e) => setIri(e.target.value)}
                placeholder="DirectAirCapturePlant"
              />
              <p className="mt-1 text-small text-slate-500">
                Will be saved as <code className="font-mono">ett:{stripPrefix(iri)}</code>
              </p>
            </div>
            <div>
              <label className="block text-h4 text-slate-700">Label</label>
              <Input value={label} onChange={(e) => setLabel(e.target.value)} />
            </div>
            <div>
              <label className="block text-h4 text-slate-700">Parent IRI</label>
              <Input
                value={parentIri}
                onChange={(e) => setParentIri(e.target.value)}
                placeholder="ett:Asset"
              />
            </div>
          </div>

          <div>
            <label className="block text-h4 text-slate-700">Comment / rationale</label>
            <Textarea
              rows={3}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Why this concept is needed; sources / evidence; SHACL constraints to apply later"
            />
          </div>
        </CardContent>
      </Card>

      <div className="flex flex-wrap gap-2">
        <Button onClick={() => save.mutate()} disabled={save.isPending || !iri || !label}>
          {save.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
          {draftId ? 'Save changes' : 'Save draft'}
        </Button>
        <Button
          variant="secondary"
          onClick={() => runSandbox.mutate()}
          disabled={!draftId || runSandbox.isPending}
        >
          Run sandbox
        </Button>
        <Button
          variant="secondary"
          onClick={() => requestPromotion.mutate()}
          disabled={!draftId || draftStatus !== 'sandbox_run'}
        >
          Request promotion
        </Button>
        <Button variant="ghost" onClick={() => navigate('/studio/ontology')}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

// ─── Helpers ────────────────────────────────────────────────────

function buildInitialComment(label: string, frequency: string): string {
  if (!label) return '';
  const freq = Number.parseInt(frequency, 10);
  const freqLine = Number.isFinite(freq) && freq > 0
    ? `Pipeline observed this entity ${freq} time${freq === 1 ? '' : 's'} without a matching class.`
    : '';
  return [freqLine, ''].filter(Boolean).join('\n');
}

function toCamelCase(label: string): string {
  return label
    .split(/[\s-_]+/)
    .filter(Boolean)
    .map((w) => w[0]!.toUpperCase() + w.slice(1).toLowerCase())
    .join('');
}

function ensureEttPrefix(iri: string): string {
  return iri.includes(':') ? iri : `ett:${iri}`;
}

function stripPrefix(iri: string): string {
  return iri.includes(':') ? (iri.split(':').pop() ?? iri) : iri;
}
